import { useEffect, useState } from 'react'
import useFetch from '../../../_fetch'
import Select from 'react-select'
import { FiClipboard } from 'react-icons/fi'  // Importer et ikon for copy-knappen

type IPublisher = {
  id: string
  name: string
  url: string
  commission: number
  network: string
  country: string
  publisherId: string
  publisherName: string
  shopUrl: string
  credentialsId: string
}

function Flag(opts: { country: string }) {
  if (!opts.country || opts.country?.toUpperCase() === 'UNKNOWN') return null

  const flagUrl = `/flags/${opts.country.toLowerCase()}.svg`
  return <img className="me-1" src={flagUrl} alt={opts.country} style={{ height: '12px' }} />
}

export default function LinkBuilder() {
  const { doFetch } = useFetch()

  const [publishers, setPublishers] = useState<IPublisher[]>([])
  const [selectedPublisher, setSelectedPublisher] = useState<any>(null)
  const [filteredPublishers, setFilteredPublishers] = useState<IPublisher[]>([])
  const [selectedCampaign, setSelectedCampaign] = useState<any>(null)
  const [clickReference, setClickReference] = useState('')
  const [deepLink, setDeepLink] = useState('')
  const [generatedResult, setGeneratedResult] = useState('')

  const fetchPublishers = async () => {
    const res = await doFetch('/publisher-campaigns/all')
    const json = await res.json() as IPublisher[]
    setPublishers(json)
    
    const filtered = [...json].sort((a, b) => a.name.localeCompare(b.name))
    setFilteredPublishers(filtered)
  }

  useEffect(() => {
    fetchPublishers()
  }, [])

  const publisherGroup: { [key: string]: { 
    network: string 
    publisherId: string 
    publisherName: string 
    countries: string[] } 
  } = {}

  for (const publisher of publishers) {
    const { country, network, publisherId, publisherName } = publisher
    const key = `${network}-${publisherId}`
    if (!publisherGroup[key]) {
      publisherGroup[key] = { network, publisherId, publisherName, countries: [] }
    }
    if (!publisherGroup[key].countries.includes(country)) {
      publisherGroup[key].countries.push(country) // Add unique countries
    }
  }

  const options = Object.keys(publisherGroup).map((key) => {
    const publisher = publisherGroup[key]
    return {
      value: `${publisher.network} ${publisher.publisherName}`,
      label: (
        <div>
          <div>
            <strong>{publisher.network}</strong> - {publisher.publisherName}
          </div>
          <div style={{ marginTop: '4px', display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
            {publisher.countries.map((country) => (
              <Flag key={country} country={country} />
            ))}
          </div>
        </div>
      ),
      searchableLabel: `${publisher.network} ${publisher.countries.join(' ')} ${publisher.publisherName}`,
      network: publisher.network,
      publisherId: publisher.publisherId,
      publisherName: publisher.publisherName,
    }
  })

  const handlePublisherChange = (selectedOption: any) => {
    setSelectedPublisher(selectedOption)
    setSelectedCampaign(null)
    const { network, publisherId } = selectedOption

    const filtered = publishers.filter(
      (publisher) => publisher.network === network && publisher.publisherId === publisherId
    )

    const filteredSorted = [...filtered].sort((a, b) => a.name.localeCompare(b.name))

    setFilteredPublishers(filteredSorted)
  }

  const handleCampaignChange = (selectedOption: any) => {
    const campaign = publishers.find(
      (f) =>
        f.id === selectedOption.id &&
      selectedOption.network === f.network &&
      selectedOption.publisherId === f.publisherId
    )

    setSelectedCampaign({campaign, ...selectedOption})
  }

  const handleGenerateClick = () => {
    const campaign = selectedCampaign?.campaign as IPublisher
    if(!campaign) return

    const buildNordparLink = (rawDeepLink: string, agreementId: string, publisherId: string, clickRef: string) => {
      const trimmed = String(rawDeepLink || '').trim()
      if (!trimmed) return ''

      const normalizedAgreementId = String(agreementId || '').trim()
      const normalizedPublisherId = String(publisherId || '').trim()
      if (!normalizedAgreementId) return ''

      const refValue = normalizedPublisherId && !normalizedAgreementId.endsWith(`-${normalizedPublisherId}`)
        ? `${normalizedAgreementId}-${normalizedPublisherId}`
        : normalizedAgreementId

      const withProtocol = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed.replace(/^\/+/, '')}`

      try {
        const parsed = new URL(withProtocol)

        parsed.searchParams.set('np-ref', refValue)
        if (String(clickRef || '').trim()) {
          parsed.searchParams.set('np-click-ref', String(clickRef).trim())
        }

        return parsed.toString()
      } catch {
        const hasQuery = withProtocol.includes('?')
        const separator = hasQuery ? '&' : '?'
        const encodedRef = encodeURIComponent(refValue)
        const encodedClickRef = String(clickRef || '').trim() ? `&np-click-ref=${encodeURIComponent(String(clickRef).trim())}` : ''
        return `${withProtocol}${separator}np-ref=${encodedRef}${encodedClickRef}`
      }
    }

    if (campaign.url.startsWith('NP-')) {
      const agreementId = campaign.url.slice(3).trim()
      const publisherId = String(campaign.publisherId || '').trim()
      if (!agreementId) {
        setGeneratedResult('')
        return
      }

      setGeneratedResult(buildNordparLink(deepLink, agreementId, publisherId, clickReference))
      return
    }

    const getFinalDeepLink = () => {
        if(campaign.network !== 'Daisycon') return deepLink
        try {
            const url = new URL(deepLink)
            
            if(url.pathname.startsWith('/')) return url.pathname.slice(1)

            return url.pathname
        } catch {
            return deepLink
        }
    }
    const finalDeepLink = getFinalDeepLink()
    const finalLink = campaign.url.replace('__ref__', clickReference).replace('__dl__', encodeURIComponent(finalDeepLink))
    
    setGeneratedResult(finalLink)
  }

  const handleCopyClick = () => {
    navigator.clipboard.writeText(generatedResult).then(() => {
    })
  }

  return (
    <div className='row'>
      <h3>Link Builder</h3>
      <div className="col-8 mb-3">
        <label htmlFor="publisher-select" className="form-label">Publisher</label>
        <Select
          id="publisher-select"
          options={options}
          getOptionLabel={(e: any) => e.label}
          getOptionValue={(e: any) => e.value}
          placeholder="Select Publisher..."
          isSearchable
          onChange={handlePublisherChange}
          filterOption={(candidate, input) => {
            if (!input) return true
            const searchWords = input.toLowerCase().split(/\s+/)
            const candidateText = candidate.data.searchableLabel.toLowerCase()
            return searchWords.every((word) => candidateText.includes(word))
          }}
        />
      </div>

      <div className="col-8 mb-3">
        <label htmlFor="campaign-select" className="form-label">Campaign</label>
        <Select
            id="campaign-select"
            value={selectedCampaign}
            options={filteredPublishers.map((publisher) => ({
                value: publisher.id,
                label: (
                    <>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span>
                            <Flag country={publisher.country} /> {publisher.name}
                            </span>
                            {publisher.commission > 0 && (
                            <span style={{ fontSize: '0.9em', color: '#555' }}>
                                {publisher.commission.toFixed(2)}%
                            </span>
                            )}
                        </div>
                        <div><small><b>{publisher.network} ({publisher.publisherName})</b></small></div>
                    </>
                ),
                searchableLabel: `${publisher.name} ${publisher.country} ${
                publisher.commission > 0 ? publisher.commission.toFixed(2) : ''
                }`,
                ...publisher
            }))}
            getOptionLabel={(e: any) => e.label}
            getOptionValue={(e: any) => e.value}
            placeholder="Select campaign"
            isSearchable
            onChange={handleCampaignChange}
            filterOption={(candidate, input) => {
                if (!input) return true
                const searchWords = input.toLowerCase().split(/\s+/)
                const candidateText = candidate.data.searchableLabel.toLowerCase()
                return searchWords.every((word) => candidateText.includes(word))
            }}
            />

      </div>
        <div className="col-5">
          <label htmlFor="deep-link" className="form-label">Deep Link</label>
          <input
            id="deep-link"
            type="text"
            className="form-control"
            placeholder="Deep link"
            value={deepLink}
            onChange={(e) => setDeepLink(e.target.value)}
            disabled={!selectedCampaign}
          />
        </div>
        <div className="col-3">
          <label htmlFor="click-reference" className="form-label">Click Reference</label>
          <input
            id="click-reference"
            type="text"
            className="form-control"
            placeholder="Click reference"
            value={clickReference}
            onChange={(e) => setClickReference(e.target.value)}
            disabled={!selectedCampaign}
          />
        </div>
        <div className='col-12'></div>
        <div className="col-sm-2">
          <label className="form-label" dangerouslySetInnerHTML={{__html:'&nbsp;'}}></label>
          <button 
            className="btn btn-primary w-100" 
            onClick={handleGenerateClick} 
            disabled={!selectedCampaign}
          >
            Generate link
          </button>
        </div>
      
      <div className="row mt-3">
        <div className="col-8">
          <label htmlFor="result" className="form-label">Generated Link</label>
          <div className="position-relative">
            <textarea
              id="result"
              className="form-control"
              rows={2}
              value={generatedResult}
              readOnly
              disabled={!selectedCampaign}
              style={{
                paddingRight: '40px',
                borderRadius: '8px',
                boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
              }}
            />
            <button
              className="bg-white border-0 btn btn-white end-0 position-absolute top-50 translate-middle-y me-2"
              onClick={handleCopyClick}
              disabled={!selectedCampaign}
              style={{
                padding: '10px 15px',
                borderRadius: '8px',
                backgroundColor: '#f8f9fa',
                color: '#007bff',
                border: '1px solid #ddd',
              }}
              title="Copy link"
            >
              <FiClipboard size={20} />
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
